import * as React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  Modal,
  Card,
  CardContent,
  Chip,
  Grid
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { User, TeamSeason, PublicUserInfo } from '../../../sdk/model';
import { useNavigate } from 'react-router-dom';
import { useTeamOperations } from '../../../hooks/useTeamOperations';
import { useSnackbar } from 'notistack';
import { useAppSelector } from '../../../app/hooks';
import UserInfoCard from '../../cards/userCard'

// Define a type that includes all possible sortable fields
type SortableField = keyof TeamSeason | 'pointsFor' | 'pointsAgainst' | 'record' | 'team';

// Extend the base TeamSeason type with additional properties we need
interface TeamSeasonWithPoints extends Omit<TeamSeason, 'owners'> {
  pointsFor?: number;
  pointsAgainst?: number;
  teamName?: string;
  teamPicture?: string;
  owners?: PublicUserInfo[]; // Keep as PublicUserInfo[] to match the parent type
  wins?: number;
  losses?: number;
  ties?: number;
  overallRank?: number;
}

// Helper type to make all fields optional for the purpose of merging
type PartialTeamSeasonWithPoints = Partial<TeamSeasonWithPoints>;

// Type guard to check if a key is a custom sort key
const isCustomSortKey = (key: SortableField): key is 'pointsFor' | 'pointsAgainst' | 'record' | 'team' => {
  return ['pointsFor', 'pointsAgainst', 'record', 'team'].includes(key);
};


interface OverallStandingsProps {
  teamSeasons: TeamSeasonWithPoints[];
  leagueId: string;
  joinEnabled?: boolean;
  onJoinSuccess?: () => void;
  waiverPriorityOrder: string[];
}

// Helper function to safely get owner display name
const getOwnerDisplayName = (owner: PublicUserInfo): string => {
  if (!owner) return 'Owner';
  // Use type assertion to access potential properties
  const ownerAny = owner as any;
  return ownerAny.displayName || ownerAny.username || ownerAny.email || 'Owner';
};

// Helper function to safely get owner picture
const getOwnerPicture = (owner: PublicUserInfo): string | undefined => {
  if (!owner) return undefined;
  // Use type assertion to access potential picture property
  return (owner as any).picture || undefined;
};

const OverallStandings: React.FC<OverallStandingsProps> = ({
  teamSeasons,
  leagueId,
  joinEnabled = false,
  onJoinSuccess,
  waiverPriorityOrder
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useAppSelector((state) => state.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { joinTeam, isJoining } = useTeamOperations({
    currentUserId: currentUser?.id,
    onSuccess: () => {
      enqueueSnackbar('Successfully joined the team!', { variant: 'success' });
      onJoinSuccess?.();
    }
  });

  const [sortConfig, setSortConfig] = React.useState<{
    key: SortableField;
    direction: 'asc' | 'desc';
  }>({
    key: 'overallRank',
    direction: 'asc',
  });
  
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [viewingUser, setViewingUser] = React.useState<User | undefined>(undefined);

  const handleSort = (key: SortableField) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedTeamSeasons = React.useMemo(() => {
    if (!teamSeasons) return [];
    
    // Cast teamSeasons to TeamSeasonWithPoints[] since we know the shape
    const typedTeamSeasons = teamSeasons as unknown as TeamSeasonWithPoints[];
    
    return [...typedTeamSeasons].sort((a, b) => {
      const teamA = a as TeamSeasonWithPoints;
      const teamB = b as TeamSeasonWithPoints;
      
      // Handle custom sort keys
      if (isCustomSortKey(sortConfig.key)) {
        switch (sortConfig.key) {
          case 'pointsFor':
          case 'pointsAgainst': {
            const aValue = (teamA as any)[sortConfig.key] ?? 0;
            const bValue = (teamB as any)[sortConfig.key] ?? 0;
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
          }
          case 'record': {
            // Sort by wins, then by pointsFor
            const aWins = teamA.wins ?? 0;
            const bWins = teamB.wins ?? 0;
            if (aWins !== bWins) {
              return sortConfig.direction === 'asc' ? aWins - bWins : bWins - aWins;
            }
            // If wins are equal, sort by pointsFor
            return sortConfig.direction === 'asc'
              ? (teamA.pointsFor ?? 0) - (teamB.pointsFor ?? 0)
              : (teamB.pointsFor ?? 0) - (teamA.pointsFor ?? 0);
          }
          case 'team':
            return sortConfig.direction === 'asc'
              ? (teamA.teamName || '').localeCompare(teamB.teamName || '')
              : (teamB.teamName || '').localeCompare(teamA.teamName || '');
          default:
            return 0; // Should never happen
        }
      }
      
      // Handle standard TeamSeasonWithPoints keys
      const aValue = teamA[sortConfig.key as keyof TeamSeasonWithPoints];
      const bValue = teamB[sortConfig.key as keyof TeamSeasonWithPoints];
      
      // Default to 0 for undefined values
      const compareA = aValue ?? 0;
      const compareB = bValue ?? 0;
      
      if (compareA === compareB) return 0;
      return sortConfig.direction === 'asc' 
        ? compareA < compareB ? -1 : 1 
        : compareA > compareB ? -1 : 1;
    });
  }, [teamSeasons, sortConfig]);

  const navigateToTeam = (teamId?: string) => {
    if (teamId) {
      navigate(`/league/${leagueId}/team/${teamId}`);
    }
  };

  const handleJoinTeam = async (e: React.MouseEvent<HTMLButtonElement>, teamId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      enqueueSnackbar('You must be logged in to join a team', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      await joinTeam(teamId);
      enqueueSnackbar('Successfully joined the team!', { variant: 'success' });
      onJoinSuccess?.();
    } catch (error) {
      console.error('Error joining team:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to join team', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSortableHeader = (label: string, sortKey: SortableField) => (
    <TableSortLabel
      active={sortConfig.key === sortKey}
      direction={sortConfig.direction}
      onClick={() => handleSort(sortKey)}
    >
      {label}
    </TableSortLabel>
  );

  // Mobile card component for individual teams
  const TeamCard: React.FC<{ teamSeason: TeamSeasonWithPoints; index: number }> = ({ 
    teamSeason, 
    index 
  }) => {
    const isOwner = teamSeason.owners?.some(owner => owner.id === currentUser?.id);
    
    return (
      <Card 
        sx={{ 
          mb: 1,
          border: isOwner ? `2px solid ${theme.palette.primary.main}` : '1px solid',
          borderColor: isOwner ? theme.palette.primary.main : 'divider',
          borderLeft: isOwner ? `4px solid ${theme.palette.primary.main}` : '1px solid',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Chip 
                label={`#${teamSeason.overallRank || index + 1}`} 
                size="small" 
                color="primary"
                sx={{ fontWeight: 'bold', minWidth: 40 }}
              />
              {teamSeason.teamPicture && (
                <Avatar 
                  src={teamSeason.teamPicture} 
                  sx={{ width: 32, height: 32 }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Button 
                  onClick={() => navigateToTeam(teamSeason.teamId)}
                  sx={{ p: 0, justifyContent: 'flex-start', minWidth: 'auto' }}
                >
                  <Typography variant="body2" fontWeight="medium" sx={{ textAlign: 'left' }}>
                    {teamSeason.teamName || 'Unnamed Team'}
                  </Typography>
                </Button>
                {isOwner && (
                  <Chip 
                    label="YOU" 
                    size="small" 
                    color="primary" 
                    sx={{ mt: 0.5, fontSize: '0.6rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Record</Typography>
              <Typography variant="body2" fontWeight="medium">
                {teamSeason.wins}-{teamSeason.losses}
                {teamSeason.ties ? `-${teamSeason.ties}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">PF</Typography>
              <Typography variant="body2" fontWeight="medium">
                {('pointsScored' in teamSeason && typeof teamSeason.pointsScored === 'number') 
                  ? teamSeason.pointsScored.toFixed(1) : '0.0'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">PA</Typography>
              <Typography variant="body2" fontWeight="medium">
                {('pointsAgainst' in teamSeason && typeof teamSeason.pointsAgainst === 'number') 
                  ? teamSeason.pointsAgainst.toFixed(1) : '0.0'}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {teamSeason.owners?.[0] ? teamSeason.owners.map(owner => (
                <Button 
                  key={owner.id}
                  onClick={() => setViewingUser(teamSeason.owners?.[0])}
                  sx={{ p: 0, minWidth: 'auto' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {getOwnerDisplayName(owner)}
                    </Typography>
                    {getOwnerPicture(owner) && (
                      <Avatar 
                        src={getOwnerPicture(owner)} 
                        sx={{ width: 20, height: 20 }}
                      />
                    )}
                  </Box>
                </Button>
              )) : joinEnabled ? (
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => 
                    teamSeason.teamId && handleJoinTeam(e, teamSeason.teamId)
                  }
                  disabled={isJoining}
                  sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                >
                  {isJoining ? 'Joining...' : 'Join'}
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No Owner
                </Typography>
              )}
            </Box>
            
            <Chip 
              label={`W${waiverPriorityOrder ? waiverPriorityOrder.indexOf(teamSeason.teamId!) + 1 : ''}`} 
              size="small" 
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Modal open={!!viewingUser} onClose={() => setViewingUser(undefined)} 
          aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
          sx={{margin: '5%'}}>
        <UserInfoCard userId={viewingUser?.id!} callback={() => setViewingUser(undefined)} />
      </Modal>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <EmojiEventsIcon color="warning" sx={{ mr: 1 }} />
        Overall Standings
      </Typography>
      
      {isMobile ? (
        // Mobile Card Layout
        <Box sx={{ p: 1 }}>
          {sortedTeamSeasons.map((teamSeason, index) => (
            <TeamCard
              key={teamSeason.teamId}
              teamSeason={teamSeason}
              index={index}
            />
          ))}
        </Box>
      ) : (
        // Desktop Table Layout
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ 
                '& th': { 
                  fontWeight: 'bold',
                  backgroundColor: theme => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(0, 0, 0, 0.04)'
                } 
              }}>
                <TableCell>Rank</TableCell>
                <TableCell>Team</TableCell>
                <TableCell align="right">
                  {renderSortableHeader('Record', 'wins')}
                </TableCell>
                <TableCell align="right">
                  {renderSortableHeader('PF', 'pointsFor')}
                </TableCell>
                <TableCell align="right">
                  {renderSortableHeader('PA', 'pointsAgainst')}
                </TableCell>
                <TableCell align="right">Owner(s)</TableCell>
                <TableCell align="right">Waiver</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTeamSeasons.map((teamSeason, index) => (
                <TableRow 
                  key={teamSeason.teamId}
                >
                  <TableCell>{teamSeason.overallRank}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button onClick={() => navigateToTeam(teamSeason.teamId)}>
                        {teamSeason.teamPicture && (
                          <Avatar 
                            src={teamSeason.teamPicture} 
                            sx={{ width: 30, height: 30 }}
                          />
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {teamSeason.teamName || 'Unnamed Team'}
                          </Typography>
                        </Box>
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {teamSeason.wins}-{teamSeason.losses}
                    {teamSeason.ties ? `-${teamSeason.ties}` : ''}
                  </TableCell>
                  <TableCell align="right">
                    {('pointsScored' in teamSeason && typeof teamSeason.pointsScored === 'number') 
                      ? teamSeason.pointsScored.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                      : '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {('pointsAgainst' in teamSeason && typeof teamSeason.pointsAgainst === 'number') 
                      ? teamSeason.pointsAgainst.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                      : '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {teamSeason.owners?.[0] ? teamSeason.owners.map(owner =>
                      <Box key={owner.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setViewingUser(teamSeason.owners?.[0])}>
                          <Typography variant="body2">
                            {getOwnerDisplayName(owner)}
                          </Typography>
                          {getOwnerPicture(owner) && (
                            <Avatar 
                              src={getOwnerPicture(owner)} 
                              sx={{ width: 24, height: 24 }}
                            />
                          )}
                        </Button>
                      </Box>
                    ) : joinEnabled ? (
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => teamSeason.teamId && handleJoinTeam(e, teamSeason.teamId)}
                        disabled={isJoining}
                      >
                        {isJoining ? 'Joining...' : 'Join Team'}
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No Owner
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {waiverPriorityOrder ? waiverPriorityOrder.indexOf(teamSeason.teamId!) + 1 : ""}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default OverallStandings;
